import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AxiosInstance from "../Home/AxiosInstance";

export default function UserAddress() {
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [addressType, setAddressType] = useState("");
  const [stateOptions, setStateOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [addressTypeOptions, setAddressTypeOptions] = useState([]);
  const [addressSearch, setAddressSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if current path is for institution
  const isInstitution = location.pathname.includes("institution");

  useEffect(() => {
    async function fetchAddressTypes() {
      try {
        const res = await AxiosInstance.get(
          "companies/address/types/"
        );
        if (res.status === 200) setAddressTypeOptions(res.data);
      } catch (err) {
        console.error("Error fetching address types:", err);
      }
    }
    fetchAddressTypes();
  }, []);

  useEffect(() => {
    async function fetchCountries() {
      try {
        const res = await AxiosInstance.get(
          "companies/countries/"
        );
        setCountryOptions(res.data);
      } catch (err) {
        console.error("Error fetching countries:", err);
      }
    }
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!country) {
      setStateOptions([]);
      setState("");
      return;
    }
    async function fetchStates() {
      try {
        const res = await AxiosInstance.get(
          `companies/states/${country}/`
        );
        setStateOptions(res.data);
      } catch (err) {
        console.error("Error fetching states:", err);
      }
    }
    fetchStates();
  }, [country]);

  useEffect(() => {
    if (!addressSearch) {
      setSuggestions([]);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await AxiosInstance.get(
          "companies/get-address/",
          {
            params: { address: addressSearch },
          }
        );
        setSuggestions(res.data.suggestions || []);
      } catch (err) {
        console.error("Autocomplete failed", err);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [addressSearch]);

  const handleAddressSelect = async (placeId, description) => {
    setSuggestions([]);
    setAddressSearch("");

    try {
      const res = await AxiosInstance.get(
        "companies/get-address-details/",
        {
          params: { placeId },
        }
      );

      const data = res.data;

      setAddressLine1(data.addressLine1 || "");
      setAddressLine2(data.addressLine2 || "");
      setCity(data.city || "");
      setZipCode(data.postalCode || "");

      // Match by name from dropdown options
      const matchedCountry = countryOptions.find(
        (c) => c.country_name === data.country
      );
      const matchedState = stateOptions.find(
        (s) => s.state_name === data.state
      );

      setCountry(matchedCountry ? matchedCountry.country_id : "");
      setState(matchedState ? matchedState.state_id : "");
    } catch (err) {
      console.error("Error fetching address details", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("User not logged in.");
      setIsLoading(false);
      return;
    }

    const newAddress = {
      user: userId,
      address_line1: addressLine1,
      address_line2: addressLine2,
      city,
      state,
      country,
      zip_code: zipCode,
      address_type: addressType,
    };

    try {
      const res = await AxiosInstance.post(
        "companies/user/addresses/",
        newAddress
      );
      if (res.status === 200 || res.status === 201) {
        setSuccessMessage("Address added successfully!");
        setTimeout(() => navigate(-1), 2000);
      } else {
        alert("Failed to add address. Please try again.");
      }
    } catch (err) {
      console.error("Error adding address:", err);
      alert("Error adding address. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    navigate(-1);
  };

  return (
    <>
      {/* Modal backdrop */}
      <div className="modal-backdrop fade show"></div>

      <div
        className="modal d-block"
        tabIndex="-1"
        aria-modal="true"
        role="dialog"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div
          className="modal-dialog modal-dialog-centered modal-lg"
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">User Address</h5>
            </div>

            <div
              className="modal-body"
              style={{ maxHeight: "70vh", overflowY: "auto" }}
            >
              {successMessage && (
                <div className="alert alert-success" role="alert">
                  {successMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Autocomplete field */}
                <div className="mb-3 position-relative">
                  <label className="form-label">Search Address</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type address..."
                    value={addressSearch}
                    onChange={(e) => setAddressSearch(e.target.value)}
                  />
                  {suggestions.length > 0 && (
                    <ul
                      className="list-group position-absolute w-100"
                      style={{ zIndex: 999 }}
                    >
                      {suggestions.map((item, index) => (
                        <li
                          key={index}
                          className="list-group-item list-group-item-action"
                          onClick={() =>
                            handleAddressSelect(item.place_id, item.description)
                          }
                          style={{ cursor: "pointer" }}
                        >
                          {item.description}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="row mb-3">
                  <div className="col">
                    <label htmlFor="addressLine1" className="form-label">
                      Address Line 1 <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="addressLine1"
                      className="form-control"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col">
                    <label htmlFor="addressLine2" className="form-label">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      id="addressLine2"
                      className="form-control"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col">
                    <label htmlFor="city" className="form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="city"
                      className="form-control"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    />
                  </div>

                  <div className="col">
                    <label htmlFor="zipCode" className="form-label">
                      Zip Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="zipCode"
                      className="form-control"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col">
                    <label htmlFor="country" className="form-label">
                      Country <span className="text-danger">*</span>
                    </label>
                    <select
                      id="country"
                      className="form-select"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      required
                    >
                      <option value="">Select Country</option>
                      {countryOptions.length > 0 ? (
                        countryOptions.map((c) => (
                          <option key={c.country_id} value={c.country_id}>
                            {c.country_name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No countries available</option>
                      )}
                    </select>
                  </div>

                  <div className="col">
                    <label htmlFor="state" className="form-label">
                      State <span className="text-danger">*</span>
                    </label>
                    <select
                      id="state"
                      className="form-select"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      disabled={!country}
                      required
                    >
                      <option value="">Select State</option>
                      {stateOptions.length > 0 ? (
                        stateOptions.map((s) => (
                          <option key={s.state_id} value={s.state_id}>
                            {s.state_name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No states available</option>
                      )}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="addressType" className="form-label">
                    Address Type
                  </label>
                  <select
                    id="addressType"
                    className="form-select"
                    value={addressType}
                    onChange={(e) => setAddressType(e.target.value)}
                  >
                    <option value="">Select address type</option>
                    {addressTypeOptions.length > 0 ? (
                      addressTypeOptions.map((a) => (
                        <option
                          key={a.address_type_id}
                          value={a.address_type_id}
                        >
                          {a.address_type}
                        </option>
                      ))
                    ) : (
                      <option disabled>No address types available</option>
                    )}
                  </select>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save Address"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
